import React, { createContext, useContext, useCallback } from 'react';
import { useAuctionState } from './AuctionStateContext';
import { useContribution } from './ContributionContext';
import { useAffiliate } from './AffiliateContext';

// Create the context
export const CleanAuctionContext = createContext();

export const useAuction = () => {
    const context = useContext(CleanAuctionContext);
    if (!context) {
        throw new Error('useAuction must be used within a CleanAuctionProvider');
    }
    return context;
};

export const CleanAuctionProvider = ({ children }) => {
    // Get data and methods from specialized contexts
    const auctionState = useAuctionState();
    const contribution = useContribution();
    const affiliate = useAffiliate();
    
    // Destructure values from contexts for easier access
    const {
        // Constants
        TOTAL_DAYS,
        SOL_PRICE_USD,
        TOTAL_AUCTION_TOKENS,
        DAILY_ALLOCATION,
        startDate,
        startPrice,
        circulatingSupply,
        
        // State
        currentDay,
        selectedDay,
        setSelectedDay,
        auctionState: auctionStateData,
        
        // Stats
        totalSolRaised,
        currentPrice,
        dexPrice,
        moonshot,
        marketCap,
        
        // Methods
        getDayInfo,
        getAllDaysInfo,
        getDayAllocation,
        calculateAuctionStats,
        calculateDailyValues,
        getBonusBadge,
        updateTotalSolRaised
    } = auctionState;
    
    const {
        // State
        userContributions,
        projectedContributions,
        confirmedJinu,
        pendingJinu,
        isLoading: contributionIsLoading,
        
        // Methods
        addContribution,
        addProjectedContribution,
        clearProjectedContributions,
        getUserStats,
        getDayStatsWithoutProjection,
        getDayStatsWithProjection,
        calculateSharePercentage,
        calculateConfirmedJinu,
        calculatePendingJinu
    } = contribution;
    
    const {
        // State
        affiliateStats,
        referralTasks,
        isLoading: affiliateIsLoading,
        
        // Methods
        loadAffiliateStats,
        generateReferralLink,
        setReferrer,
        completeReferralTask,
        calculateAffiliateRewards,
        claimRewards,
        isReferral,
        getReferrerStats
    } = affiliate;
    
    // Map to original AuctionContext API
    const contextValue = {
        // Constants
        TOTAL_DAYS,
        SOL_PRICE_USD,
        TOTAL_AUCTION_TOKENS,
        DAILY_ALLOCATION,
        AUCTION_START_DATE: startDate,
        START_PRICE: startPrice,
        CIRCULATING_SUPPLY: circulatingSupply,
        
        // State
        currentDay,
        selectedDay,
        setSelectedDay,
        auctionState: auctionStateData,
        userContributions,
        projectedContributions,
        
        // Stats
        totalSolRaised,
        currentPrice,
        dexPrice,
        moonshot,
        marketCap,
        confirmedJinu,
        pendingJinu,
        totalJinu: confirmedJinu + pendingJinu,
        
        // Affiliate
        referralLink: affiliateStats.referralLink,
        referrer: affiliateStats.referrer,
        affiliateStats,
        referralTasks,
        
        // Loading states
        isLoading: contributionIsLoading || affiliateIsLoading,
        
        // Day info methods
        getDayInfo,
        getAllDaysInfo,
        getDayAllocation,
        calculateDailyValues,
        
        // Contribution methods
        addContribution,
        handleContribute: addContribution, // Alias for backward compatibility
        addProjectedContribution,
        clearProjectedContributions,
        getUserStats,
        getDayStatsWithoutProjection,
        getDayStatsWithProjection,
        calculateSharePercentage,
        
        // Calculation methods
        calculateAuctionStats,
        calculateConfirmedJinu,
        calculatePendingJinu,
        getBonusBadge,
        
        // Affiliate methods
        loadAffiliateStats,
        generateReferralLink,
        setReferrer,
        completeReferralTask,
        calculateAffiliateRewards,
        claimRewards: claimRewards,
        claimAffiliateRewards: claimRewards, // Alias for backward compatibility
        isReferral,
        getReferrerStats,
        copyReferralLink: () => {
            if (!affiliateStats.referralLink) return false;
            try {
                navigator.clipboard.writeText(affiliateStats.referralLink);
                return true;
            } catch (error) {
                console.error("Copy referral link error:", error);
                return false;
            }
        },
        
        // Reserve all days functionality
        handleReserveAllDays: async (amount) => {
            if (!amount || amount <= 0) return { success: false, error: "Invalid amount" };
            
            try {
                // Calculate amount per day
                const remainingDays = TOTAL_DAYS - currentDay + 1;
                const amountPerDay = parseFloat(amount) / remainingDays;
                
                // Add contribution for each remaining day
                for (let day = currentDay; day <= TOTAL_DAYS; day++) {
                    await addContribution(amountPerDay);
                }
                
                return { success: true };
            } catch (error) {
                console.error("Reserve all days error:", error);
                return { success: false, error: error.message };
            }
        },
        
        // Update total SOL raised
        updateTotalSolRaised
    };
    
    return (
        <CleanAuctionContext.Provider value={contextValue}>
            {children}
        </CleanAuctionContext.Provider>
    );
};
